import { useNavigate } from "react-router-dom";
import { Send, ArrowUpRight, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import { PersonAvatar } from "@/components/PersonAvatar";
import { channelMeta } from "@/components/ChannelBadge";
import { fmtUSD, timeAgo } from "@/lib/api";

const StepDot = ({ step, state }) => {
  const m = channelMeta(step.channel);
  const Icon = m.icon;
  const styles = {
    fired: { background: m.bg, color: m.color, border: "1px solid transparent" },
    current: { background: "rgba(79,142,247,0.1)", color: "#7FAEF9", border: "1px dashed rgba(79,142,247,0.6)" },
    pending: { background: "#161616", color: "#4a4a4a", border: "1px solid #222" },
  };
  return (
    <div className="flex flex-col items-center gap-1" title={`${m.label} · ${step.tone} tone · after ${step.wait_hours}h`}>
      <span className={`flex h-7 w-7 items-center justify-center rounded-full ${state === "current" ? "pulse-dot" : ""}`}
        style={styles[state]}>
        <Icon size={11} />
      </span>
      <span className={`text-[8.5px] uppercase tracking-wide ${state === "pending" ? "text-[#3d3d3d]" : "text-[#6b6b6b]"}`}>
        {m.label}
      </span>
    </div>
  );
};

const StepChain = ({ steps, progress }) => (
  <div className="flex items-start gap-1.5" data-testid="chase-step-chain">
    {steps.map((s, i) => {
      const state = i < progress.completed ? "fired" : i === progress.current_index && !progress.exhausted ? "current" : "pending";
      return (
        <div key={i} className="flex items-start gap-1.5">
          {i > 0 && (
            <span className="mt-2 flex items-center gap-0.5 text-[9px] tabular-nums text-[#4a4a4a]">
              {s.wait_hours}h <ArrowRight size={8} />
            </span>
          )}
          <StepDot step={s} state={state} />
        </div>
      );
    })}
  </div>
);

export const ChaseRow = ({ chase, onNudge, nudging }) => {
  const navigate = useNavigate();
  const { employee: emp, strategy, progress, current_step: current } = chase;
  const nextMeta = current ? channelMeta(current.channel) : null;

  return (
    <div data-testid={`chase-row-${emp.id}`} data-aria-key={`employee:${emp.id}`} data-aria-label={emp.name}
      className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-[14px] border border-[#222] bg-[#111111] px-4 py-3.5 card-shadow transition-colors hover:border-[#2c2c2c]">
      {/* who */}
      <button onClick={() => navigate(`/employees/${emp.id}`)} className="flex w-52 min-w-0 items-center gap-2.5 text-left">
        <PersonAvatar name={emp.name} size={34} color={emp.avatar_color} />
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-medium text-white">{emp.name}</span>
          <span className="block truncate text-[10.5px] text-[#6b6b6b]">{emp.role} · {emp.department}</span>
        </span>
      </button>

      {/* what they owe */}
      <div className="w-40">
        <div className="text-[13px] font-semibold tabular-nums text-white">{fmtUSD(chase.outstanding_amount)}</div>
        <div className="mt-0.5 truncate text-[10.5px] text-[#6b6b6b]" title={chase.expenses.map((e) => e.merchant).join(", ")}>
          {chase.outstanding_count} receipt{chase.outstanding_count === 1 ? "" : "s"} · {chase.expenses.map((e) => e.merchant).slice(0, 2).join(", ")}
          {chase.expenses.length > 2 && ` +${chase.expenses.length - 2}`}
        </div>
      </div>

      {/* playbook progress */}
      <div className="min-w-0">
        <div className="mb-1.5 flex items-center gap-1.5 text-[10px] text-[#6b6b6b]">
          <span className="rounded-md bg-[rgba(79,142,247,0.1)] px-1.5 py-px font-medium text-[#7FAEF9]" data-testid={`chase-strategy-${emp.id}`}>
            {strategy?.name || "No strategy"}
          </span>
          {strategy?.inherited_default && <span className="text-[#4a4a4a]">workspace default</span>}
        </div>
        {strategy && <StepChain steps={strategy.steps} progress={progress} />}
      </div>

      {/* next action + last touch */}
      <div className="ml-auto flex flex-col items-end gap-1 text-right">
        {progress.exhausted ? (
          <span className="flex items-center gap-1.5 rounded-md bg-[rgba(245,158,11,0.12)] px-2 py-1 text-[11px] font-medium text-[#FBBF24]" data-testid={`chase-escalated-${emp.id}`}>
            <AlertTriangle size={11} /> Playbook exhausted — needs you
          </span>
        ) : (
          <span className="text-[11.5px] text-[#c9c9c9]" data-testid={`chase-next-${emp.id}`}>
            Next: <span style={{ color: nextMeta?.color }}>{nextMeta?.label}</span>
            <span className="text-[#6b6b6b]"> ({current?.tone}) in </span>
            <span className="tabular-nums font-medium text-white">{progress.next_eta_hours}h</span>
          </span>
        )}
        <span className="text-[10px] text-[#555]">
          {chase.last_contact ? `Last touch ${timeAgo(chase.last_contact.ts)} · ${channelMeta(chase.last_contact.channel).label}` : "Not contacted yet"}
        </span>
      </div>

      {/* actions */}
      <div className="flex items-center gap-1.5">
        <button onClick={() => onNudge(chase)} disabled={nudging === emp.id} data-testid={`chase-nudge-${emp.id}`}
          className="flex h-7 items-center gap-1.5 rounded-lg bg-[rgba(79,142,247,0.12)] px-2.5 text-[11.5px] font-medium text-[#7FAEF9] transition-colors hover:bg-[rgba(79,142,247,0.24)] hover:text-white disabled:opacity-50">
          {nudging === emp.id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Nudge now
        </button>
        <button onClick={() => navigate(`/employees/${emp.id}`)} data-testid={`chase-thread-${emp.id}`} title="Open thread"
          className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#1c1c1c] text-[#A3A3A3] transition-colors hover:bg-[#242424] hover:text-white">
          <ArrowUpRight size={12} />
        </button>
      </div>
    </div>
  );
};
